import React, { useState } from "react";
import dayjs from "dayjs";
import { message } from "antd";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon, KeyIcon } from "@heroicons/react/24/outline";
import {
  EmptyState,
  PageHeader,
  SectionCard,
  StatusBadge,
  TWButton,
  TWConfirmModal,
  TWDataTable,
} from "../../components/ui";
import { DataTableSkeleton, StackedText, colTitle } from "../../data/columns";
import fleetApi from "../../services/fleetApi";
import AdminOnly from "./components/admin-only";
import DevicesNav from "./components/devices-nav";
import EnrollmentCodeModal from "./components/enrollment-code-modal";
import EnrollmentTokenModal from "./components/enrollment-token-modal";
import useFleetPoll, { fleetErrorText } from "./use-fleet-poll";
import { statusLabel } from "./fleet-utils";

const ROLE_TONES = { kiosk: "blue", kitchen: "violet", unassigned: "amber" };
const DATE_FORMAT = "MMM D, YYYY HH:mm";
const DASH = "—";

const SKELETON_HEADERS = [
  { label: "Created", w: "w-40" },
  { label: "Company › Store", w: "w-40" },
  { label: "Role", shape: "pill", w: "w-16" },
  { label: "Note", w: "w-32" },
  { label: "Expires", w: "w-32" },
  { label: "Used by", w: "w-28" },
  { label: "", align: "right", w: "w-16" },
];

const CODE_SKELETON_HEADERS = [
  { label: "Created", w: "w-40" },
  { label: "Company › Store", w: "w-40" },
  { label: "Role", shape: "pill", w: "w-16" },
  { label: "Note", w: "w-32" },
  { label: "Expires", w: "w-32" },
  { label: "Uses", w: "w-12" },
  { label: "Status", shape: "pill", w: "w-20" },
  { label: "", align: "right", w: "w-16" },
];
const CODE_STATUS = {
  active: { tone: "emerald", label: "Active" },
  locked: { tone: "amber", label: "Locked" },
  exhausted: { tone: "amber", label: "Exhausted" },
  expired: { tone: "slate", label: "Expired" },
  revoked: { tone: "rose", label: "Revoked" },
};

const formatDate = (iso) => (iso ? dayjs(iso).format(DATE_FORMAT) : DASH);
const Any = () => (
  <span className="text-slate-400 dark:text-slate-500">Any</span>
);
const isOpen = (token) =>
  !token.usedat && !token.useddeviceid && !token.revokedat;

// Too many failed attempts lock a code for 24 h: locked only while the date is ahead.
const isLocked = (code) =>
  Boolean(code.lockeduntil) && dayjs(code.lockeduntil).valueOf() > Date.now();

// Revoked first, then expired, then locked, then exhausted, else active.
const codeStatus = (code) => {
  if (code.revokedat) return "revoked";
  if (code.expiresat && dayjs(code.expiresat).isBefore(dayjs())) return "expired";
  if (isLocked(code)) return "locked";
  if (code.uses >= code.maxuses) return "exhausted";
  return "active";
};

const codeStatusLabel = (code, status) =>
  status === "locked"
    ? `Locked until ${dayjs(code.lockeduntil).format("HH:mm")}`
    : CODE_STATUS[status].label;

// A 404 means an API that predates enrollment codes (R-21).
const isNotFound = (err) => err?.response?.status === 404;

const Enroll = () => (
  <AdminOnly>
    <EnrollList />
  </AdminOnly>
);

const EnrollList = () => {
  const { data, loading, fetching, error, reload } = useFleetPoll(
    () => fleetApi.listTokens().then((res) => res.data.data),
    {
      intervalMs: 0,
      onRefetchError: (err) =>
        message.error(fleetErrorText(err, "Could not load tokens")),
    },
  );
  const rows = data || [];

  // The codes load on their own: the tokens table never waits on them, and a
  // failure (or a 404 from an older API) stays in their section.
  const {
    data: codeData,
    loading: codesLoading,
    fetching: codesFetching,
    error: codesError,
    reload: reloadCodes,
  } = useFleetPoll(
    () => fleetApi.listCodes().then((res) => res.data.data),
    {
      intervalMs: 0,
      onRefetchError: (err) =>
        message.error(fleetErrorText(err, "Could not load codes")),
    },
  );
  const codeRows = codeData || [];
  const codesHidden = isNotFound(codesError);

  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeConfirm, setCodeConfirm] = useState(null); // the code being revoked
  const [revokingCode, setRevokingCode] = useState(false);

  const closeCodeConfirm = () => {
    if (!revokingCode) setCodeConfirm(null);
  };

  const revokeCode = async () => {
    if (!codeConfirm || revokingCode) return;
    setRevokingCode(true);
    try {
      await fleetApi.revokeCode(codeConfirm.id);
      message.success("Code revoked");
      reloadCodes();
    } catch (err) {
      message.error(fleetErrorText(err, "Could not revoke the code"));
    } finally {
      setRevokingCode(false);
      setCodeConfirm(null);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // the token being revoked
  const [revoking, setRevoking] = useState(false);

  const closeConfirm = () => {
    if (!revoking) setConfirm(null);
  };

  const revoke = async () => {
    if (!confirm || revoking) return;
    setRevoking(true);
    try {
      await fleetApi.revokeToken(confirm.id);
      message.success("Token revoked");
      reload();
    } catch (err) {
      message.error(fleetErrorText(err, "Could not revoke the token"));
    } finally {
      setRevoking(false);
      setConfirm(null);
    }
  };

  const columns = [
    {
      title: colTitle("Created"),
      key: "created",
      render: (_, t) => (
        <StackedText
          primary={t.createdbyemail || DASH}
          secondary={formatDate(t.createdate)}
        />
      ),
    },
    {
      title: colTitle("Company › Store"),
      key: "store",
      render: (_, t) =>
        t.storename || t.companyname ? (
          <span className="text-sm text-slate-700 dark:text-slate-200">
            {[t.companyname, t.storename].filter(Boolean).join(" › ")}
          </span>
        ) : (
          <Any />
        ),
    },
    {
      title: colTitle("Role"),
      key: "role",
      render: (_, t) =>
        t.role ? (
          <StatusBadge
            size="sm"
            tone={ROLE_TONES[t.role] || "slate"}
            label={statusLabel(t.role)}
          />
        ) : (
          <Any />
        ),
    },
    {
      title: colTitle("Note"),
      key: "note",
      render: (_, t) =>
        t.note ? (
          <span
            className="block max-w-[16rem] truncate text-sm text-slate-600 dark:text-slate-300"
            title={t.note}
          >
            {t.note}
          </span>
        ) : (
          DASH
        ),
    },
    {
      title: colTitle("Expires"),
      key: "expires",
      render: (_, t) => (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {formatDate(t.expiresat)}
        </span>
      ),
    },
    {
      title: colTitle("Used by"),
      key: "usedby",
      render: (_, t) => {
        if (t.useddeviceid) {
          return (
            <Link
              to={`/dashboard/devices/${t.useddeviceid}`}
              className="text-sm font-medium text-blue-600 hover:underline dark:text-sky-400"
            >
              {t.useddevicename || t.useddeviceid}
            </Link>
          );
        }
        if (t.revokedat)
          return <StatusBadge size="sm" tone="slate" label="Revoked" />;
        return (
          <span className="text-sm text-slate-400 dark:text-slate-500">
            Unused
          </span>
        );
      },
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: "actions",
      align: "right",
      render: (_, t) =>
        isOpen(t) ? (
          <TWButton
            variant="ghost"
            size="sm"
            className="text-rose-600 hover:text-rose-700 dark:text-rose-400"
            onClick={() => setConfirm(t)}
          >
            Revoke
          </TWButton>
        ) : null,
    },
  ];

  const codeColumns = [
    columns[0],
    columns[1],
    columns[2],
    columns[3],
    columns[4],
    {
      title: colTitle("Uses"),
      key: "uses",
      render: (_, c) => (
        <span className="text-sm tabular-nums text-slate-600 dark:text-slate-300">
          {c.uses} / {c.maxuses}
        </span>
      ),
    },
    {
      title: colTitle("Status"),
      key: "status",
      render: (_, c) => {
        const status = codeStatus(c);
        return (
          <StatusBadge
            size="sm"
            tone={CODE_STATUS[status].tone}
            label={codeStatusLabel(c, status)}
          />
        );
      },
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: "actions",
      align: "right",
      render: (_, c) =>
        ["active", "locked"].includes(codeStatus(c)) ? (
          <TWButton
            variant="ghost"
            size="sm"
            className="text-rose-600 hover:text-rose-700 dark:text-rose-400"
            onClick={() => setCodeConfirm(c)}
          >
            Revoke
          </TWButton>
        ) : null,
    },
  ];

  const openCreate = () => setModalOpen(true);
  const openCreateCode = () => setCodeModalOpen(true);

  let codesBody;
  if (codesError) {
    codesBody = (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load enrollment codes"
        description={fleetErrorText(codesError)}
        action={<TWButton onClick={reloadCodes}>Retry</TWButton>}
      />
    );
  } else if (codesLoading) {
    codesBody = <DataTableSkeleton headers={CODE_SKELETON_HEADERS} rows={3} />;
  } else if (codeRows.length === 0) {
    codesBody = (
      <EmptyState
        icon={KeyIcon}
        title="No enrollment codes"
        description="Create a code to enroll a tablet by typing it."
        action={
          <TWButton variant="primary" onClick={openCreateCode}>
            New enrollment code
          </TWButton>
        }
      />
    );
  } else {
    codesBody = (
      <div className="reveal">
        <TWDataTable
          columns={codeColumns}
          data={codeRows}
          loading={codesFetching}
        />
      </div>
    );
  }

  let body;
  if (error) {
    body = (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load tokens"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  } else if (loading) {
    body = <DataTableSkeleton headers={SKELETON_HEADERS} rows={6} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon={KeyIcon}
        title="No enrollment tokens"
        description="Create one to enroll a new tablet."
        action={
          <TWButton variant="primary" onClick={openCreate}>
            New enrollment token
          </TWButton>
        }
      />
    );
  } else {
    body = (
      <div className="reveal">
        <TWDataTable columns={columns} data={rows} loading={fetching} />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title="Enrollment"
          description="Codes and one-time tokens that let a new tablet join the fleet."
          actions={
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <TWButton
                variant={codesHidden ? "primary" : "ghost"}
                onClick={openCreate}
              >
                New enrollment token
              </TWButton>
              {codesHidden ? null : (
                <TWButton variant="primary" onClick={openCreateCode}>
                  New enrollment code
                </TWButton>
              )}
            </div>
          }
        />
        <DevicesNav current="enroll" />
        {codesHidden ? null : (
          <SectionCard
            title="Enrollment codes"
            description="Type a short code on the tablet instead of a token."
            noBodyPadding
          >
            {codesBody}
          </SectionCard>
        )}
        <SectionCard title="Enrollment tokens" noBodyPadding>
          {body}
        </SectionCard>
      </div>

      {codesHidden ? null : (
        <EnrollmentCodeModal
          open={codeModalOpen}
          onClose={() => setCodeModalOpen(false)}
          onCreated={reloadCodes}
        />
      )}

      <EnrollmentTokenModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={reload}
      />

      <TWConfirmModal
        open={Boolean(confirm)}
        onClose={closeConfirm}
        onConfirm={revoke}
        loading={revoking}
        tone="danger"
        title="Revoke this token?"
        description="A tablet can no longer enroll with it. Devices already enrolled are not affected."
        confirmLabel="Revoke"
      />

      <TWConfirmModal
        open={Boolean(codeConfirm)}
        onClose={closeCodeConfirm}
        onConfirm={revokeCode}
        loading={revokingCode}
        tone="danger"
        title="Revoke this code?"
        description="The code stops working right away. Tablets already enrolled are not affected."
        confirmLabel="Revoke"
      />
    </div>
  );
};

export default Enroll;
